import React from 'react';
import { useTheme } from '@emotion/react';
import {
  BulbFilled,
  BulbOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  WarningFilled,
  WarningOutlined
} from '@ant-design/icons';
// Every generated icon shares one component type, so one of them stands for the shape.
type IconComponent = typeof WarningOutlined;
type IconProps = React.ComponentProps<IconComponent>;

/**
 * The Figma Icons page mandates the outlined set, but swapping a glyph is a change today's users would
 * see, so the filled icon stays on the legacy side of the switch.
 *
 * Every pair lives here rather than at the call sites so this is the one file on the `*Filled` lint
 * allowlist instead of one per surface, and so APO-484 deletes the swap in a single file.
 */
// forwardRef because the antd icons are: a Tooltip or Popover wrapping one of these reaches for its
// ref, and a plain function component would swallow it.
const designSwitchIcon = (Legacy: IconComponent, NewDesign: IconComponent) => {
  const Icon = React.forwardRef<HTMLSpanElement, IconProps>((props, ref) => {
    const theme = useTheme();
    const Component = theme.isNewDesign ? NewDesign : Legacy;

    return <Component {...props} ref={ref} />;
  });

  Icon.displayName = `DesignSwitchIcon(${NewDesign.displayName ?? 'icon'})`;

  return Icon;
};

export const BulbIcon = designSwitchIcon(BulbFilled, BulbOutlined);
export const InfoCircleIcon = designSwitchIcon(InfoCircleFilled, InfoCircleOutlined);
export const WarningIcon = designSwitchIcon(WarningFilled, WarningOutlined);
